/**
 * @fileoverview Pure running-balance + statement-reconciliation helpers (MOR-754)
 * @module jobs/summarization/agents/bankTransactions/balanceCore
 *
 * The running balance shown in the All Transactions panel (and written to the
 * export) used to be a naive cumulative sum seeded at zero, so any account
 * whose earliest statement opened at a non-zero balance ran permanently
 * negative/offset. Bank statements print their own balance after (almost)
 * every line — extraction stores it as `balanceAfter` — so we can do better:
 *
 *   1. SEED each account's running balance so it agrees with the first stated
 *      balance (equivalent to seeding from the statement's opening balance).
 *   2. RECONCILE the computed balance against the stated balance at each
 *      subsequent anchor. A break means transactions are missing between two
 *      rows — in practice, a missing statement — and is surfaced as a gap for
 *      the practitioner to follow up.
 *   3. RE-ANCHOR at each break so a single missing statement produces one gap
 *      and the balances after it still reconcile, instead of every following
 *      row inheriting the error.
 *
 * Statement-balance polarity: deposit accounts print a balance that moves
 * with credits (+) / debits (−), but liability-style statements (credit
 * cards, loans) print the amount OWING, which moves the opposite way. We
 * detect the polarity per account by voting over adjacent anchored rows and
 * orient stated balances into the panel's convention (credits +) before
 * seeding/comparing, so credit-card statements neither flag spurious gaps nor
 * flip the displayed balance direction.
 *
 * Comparisons only happen on the first row of each date, and a mismatch is
 * flagged only once it PERSISTS at the next comparison point: extraction
 * order is preserved within a date but cross-document interleaving can
 * scramble same-day rows, making any single stated balance untrustworthy
 * mid-day. A day's net movement is order-independent, so a scramble's
 * mismatch vanishes by the next date boundary while a genuinely missing
 * statement's mismatch persists (and still confirms at end-of-account when
 * the data runs out — the common "last statement is missing" case).
 *
 * Contains no Convex server imports so it can run both server-side and in the
 * browser, exactly like `dedupCore` and `vouchCore`.
 */

/** Ignore sub-cent noise from float extraction; a real gap is ≥ one cent. */
export const BALANCE_TOLERANCE = 0.011;

export interface BalanceInputRow {
  /** Stable row id (entry _id in the panel, an index key in the export). */
  id: string;
  /** YYYY-MM-DD sortable date ("" when unparseable). Rows must arrive date-ascending. */
  sortableDate: string;
  /** Positive amount. */
  amount: number;
  direction: "debit" | "credit";
  /** The statement's own stated balance after this row, when printed. */
  balanceAfter?: number;
  /** Duplicates get a null balance and never move the account's balance. */
  isDuplicate?: boolean;
  /** Canonical account grouping key — balances never mix across accounts. */
  accountKey: string;
}

/** A reconciliation break — stated balance jumped away from the computed one. */
export interface BalanceGap {
  id: string;
  accountKey: string;
  sortableDate: string;
  /** Running balance computed by accumulating known transactions. */
  expected: number;
  /** The statement's stated balance, oriented into the display convention. */
  stated: number;
  /** stated − expected: the net movement missing between the statements. */
  difference: number;
}

export interface RowBalance {
  /** Null for duplicate rows. */
  runningBalance: number | null;
  /** Present when the reconciliation breaks at this row. */
  gap?: BalanceGap;
}

export interface AccountBalanceInfo {
  accountKey: string;
  /** True when at least one stated balance existed to seed/reconcile against. */
  anchored: boolean;
  /** Derived balance before the account's first row (display convention). */
  openingBalance?: number;
  /** +1 = balance moves with credits (deposit); −1 = with debits (liability). */
  orientation: 1 | -1;
  gapCount: number;
}

export interface BalanceComputation {
  byId: Map<string, RowBalance>;
  accounts: Map<string, AccountBalanceInfo>;
  /** Every reconciliation break, in row order. */
  gaps: BalanceGap[];
}

/**
 * Computes per-account running balances over rows already sorted
 * date-ascending (stable within a date, preserving statement order).
 */
export function computeRunningBalances(
  rows: BalanceInputRow[],
): BalanceComputation {
  const byId = new Map<string, RowBalance>();
  const accounts = new Map<string, AccountBalanceInfo>();
  const gaps: BalanceGap[] = [];

  const byAccount = new Map<string, BalanceInputRow[]>();
  for (const row of rows) {
    if (row.isDuplicate) {
      byId.set(row.id, { runningBalance: null });
      continue;
    }
    const arr = byAccount.get(row.accountKey) ?? [];
    arr.push(row);
    byAccount.set(row.accountKey, arr);
  }

  for (const [accountKey, accRows] of byAccount) {
    const deltas = accRows.map((r) =>
      r.direction === "credit" ? r.amount : -r.amount,
    );

    // Polarity vote: for adjacent anchored rows the stated balance must move
    // by ±delta; whichever sign fits more pairs wins (deposit +1 by default).
    const { plusVotes, minusVotes } = accRows.reduce(
      (votes, row, i) => {
        if (i === 0) return votes;
        const prev = accRows[i - 1].balanceAfter;
        const cur = row.balanceAfter;
        if (typeof prev !== "number" || typeof cur !== "number") return votes;
        const move = cur - prev;
        const fitsPlus = Math.abs(move - deltas[i]) <= BALANCE_TOLERANCE;
        const fitsMinus = Math.abs(move + deltas[i]) <= BALANCE_TOLERANCE;
        if (fitsPlus && !fitsMinus) {
          return { ...votes, plusVotes: votes.plusVotes + 1 };
        }
        if (fitsMinus && !fitsPlus) {
          return { ...votes, minusVotes: votes.minusVotes + 1 };
        }
        return votes;
      },
      { plusVotes: 0, minusVotes: 0 },
    );
    const orientation: 1 | -1 = minusVotes > plusVotes ? -1 : 1;

    // Seed so the computed balance equals the first stated balance — i.e.
    // start from the statement's opening balance instead of zero.
    let cum = 0;
    let offset = 0;
    let anchorIdx = -1;
    for (let i = 0; i < accRows.length; i++) {
      cum += deltas[i];
      const stated = accRows[i].balanceAfter;
      if (typeof stated === "number") {
        offset = orientation * stated - cum;
        anchorIdx = i;
        break;
      }
    }
    const anchored = anchorIdx >= 0;

    // `carry` accumulates CONFIRMED gap adjustments; `pending` is a candidate
    // gap awaiting confirmation at the next comparison point (or end of rows).
    // Rows written while a candidate is open are patched retroactively when it
    // confirms, so balances after a real break follow the statement's figures
    // while a self-correcting scramble leaves them untouched.
    cum = 0;
    let carry = 0;
    let pending: BalanceGap | null = null;
    let pendingRowIds: string[] = [];
    const accountGaps: BalanceGap[] = [];
    const confirmPending = () => {
      if (!pending) return;
      accountGaps.push(pending);
      for (const id of pendingRowIds) {
        const rb = byId.get(id);
        if (rb && rb.runningBalance != null) {
          rb.runningBalance += pending.difference;
        }
      }
      const gapRow = byId.get(pending.id);
      if (gapRow) gapRow.gap = pending;
      carry += pending.difference;
      pending = null;
      pendingRowIds = [];
    };

    for (let i = 0; i < accRows.length; i++) {
      const row = accRows[i];
      cum += deltas[i];
      const stated = row.balanceAfter;
      const isDateBoundary =
        i === 0 || accRows[i - 1].sortableDate !== row.sortableDate;

      if (
        anchored &&
        i > anchorIdx &&
        isDateBoundary &&
        typeof stated === "number"
      ) {
        const oriented = orientation * stated;
        let difference = oriented - (cum + offset + carry);
        if (pending) {
          if (Math.abs(difference - pending.difference) <= BALANCE_TOLERANCE) {
            // Mismatch persisted — a real break, not a scrambled day.
            confirmPending();
            difference = oriented - (cum + offset + carry);
          } else {
            // Self-corrected (same-day scramble) or superseded by a larger
            // cumulative break — re-evaluate from this row.
            pending = null;
            pendingRowIds = [];
          }
        }
        if (Math.abs(difference) > BALANCE_TOLERANCE) {
          pending = {
            id: row.id,
            accountKey,
            sortableDate: row.sortableDate,
            expected: cum + offset + carry,
            stated: oriented,
            difference,
          };
        }
      }

      byId.set(row.id, { runningBalance: cum + offset + carry });
      if (pending) pendingRowIds.push(row.id);
    }
    // No later boundary can contradict a still-pending break — confirm it so a
    // missing final statement is flagged too.
    confirmPending();

    gaps.push(...accountGaps);
    accounts.set(accountKey, {
      accountKey,
      anchored,
      openingBalance: anchored ? offset : undefined,
      orientation,
      gapCount: accountGaps.length,
    });
  }

  return { byId, accounts, gaps };
}
